import React from "react";
import { Download, ChevronDown, Trophy } from "lucide-react";

const HERO_IMG =
    "https://images.unsplash.com/photo-1696104470342-b1ed3afe8381?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwyfHxnb2xmZXIlMjBzd2luZyUyMHNpbGhvdWV0dGV8ZW58MHx8fHwxNzgxMjYzNzUwfDA&ixlib=rb-4.1.0&q=85";

export const Hero = () => {
    const scrollToLeaderboard = () => {
        document.getElementById("leaderboard")?.scrollIntoView({ behavior: "smooth" });
    };

    return (
        <section
            className="relative overflow-hidden border-b border-white/10"
            data-testid="hero-section"
        >
            {/* Background image */}
            <div className="absolute inset-0">
                <img
                    src={HERO_IMG}
                    alt="Golfer silhouette at sunset"
                    className="w-full h-full object-cover opacity-40"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-[#0B1325]/70 via-[#0B1325]/85 to-[#0B1325]" />
                <div className="absolute inset-0 bg-gradient-to-r from-[#0B1325] via-transparent to-[#0B1325]/80" />
            </div>

            <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-16 pb-24 md:pt-28 md:pb-36">
                {/* Top bar */}
                <div className="flex items-center justify-between mb-20 md:mb-28">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 border border-[#D4AF37] flex items-center justify-center">
                            <Trophy size={16} className="text-[#D4AF37]" />
                        </div>
                        <div className="font-display text-sm font-bold tracking-[0.25em] uppercase text-white">
                            Apex Majors <span className="text-[#D4AF37]">/</span>{" "}
                            <span className="text-[#94A3B8]">Trackman Series</span>
                        </div>
                    </div>
                    <div className="hidden md:flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[#94A3B8]">
                        <span className="w-1.5 h-1.5 bg-[#FF5A00] animate-pulse" />
                        Season Active
                    </div>
                </div>

                {/* Overline */}
                <div
                    className="fade-up text-[11px] tracking-[0.4em] uppercase font-bold text-[#D4AF37] mb-8"
                    style={{ animationDelay: "60ms" }}
                    data-testid="hero-overline"
                >
                    — The Official Global Standardized Major
                </div>

                {/* Headline */}
                <h1
                    className="fade-up font-display font-black uppercase text-white leading-[0.92] tracking-tighter text-4xl sm:text-6xl lg:text-7xl xl:text-8xl max-w-5xl"
                    style={{ animationDelay: "120ms" }}
                    data-testid="hero-headline"
                >
                    4 Iconic Majors.
                    <br />
                    <span className="text-white">1 Standardized Test.</span>
                    <br />
                    <span className="gold-glow text-[#D4AF37]">
                        The Hunt for Golfer of the Year.
                    </span>
                </h1>

                {/* Subline */}
                <p
                    className="fade-up mt-10 text-base md:text-lg text-[#94A3B8] max-w-2xl leading-relaxed"
                    style={{ animationDelay: "200ms" }}
                    data-testid="hero-subline"
                >
                    Four cathedrals of the game — Bethpage Black, Pebble Beach, Oakmont, and
                    Pinehurst No. 2 — rendered to identical, brutally honest Trackman conditions.
                    One leaderboard. Two divisions. A single, video-verified champion.
                </p>

                {/* CTAs */}
                <div
                    className="fade-up mt-12 flex flex-wrap items-center gap-4"
                    style={{ animationDelay: "280ms" }}
                >
                    <a
                        href="#"
                        data-testid="hero-cta-download"
                        className="group inline-flex items-center gap-3 bg-[#D4AF37] hover:bg-[#e0c050] text-black font-bold uppercase tracking-[0.15em] text-xs px-7 py-4 transition-all shadow-[0_0_30px_rgba(212,175,55,0.25)] hover:shadow-[0_0_40px_rgba(212,175,55,0.5)]"
                    >
                        <Download size={16} />
                        Download The App
                    </a>
                    <button
                        type="button"
                        onClick={scrollToLeaderboard}
                        data-testid="hero-cta-leaderboard"
                        className="group inline-flex items-center gap-3 bg-transparent border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10 font-bold uppercase tracking-[0.15em] text-xs px-7 py-4 transition-colors"
                    >
                        View Global Leaderboard
                        <ChevronDown
                            size={16}
                            className="group-hover:translate-y-0.5 transition-transform"
                        />
                    </button>
                </div>

                {/* App store badges placeholder */}
                <div
                    className="fade-up mt-6 flex items-center gap-6 text-[10px] uppercase tracking-[0.3em] text-[#475569]"
                    style={{ animationDelay: "360ms" }}
                >
                    <span data-testid="hero-app-store">App Store</span>
                    <span className="w-px h-3 bg-[#475569]" />
                    <span data-testid="hero-play-store">Google Play</span>
                </div>
            </div>

            {/* Ticker */}
            <div
                className="relative border-t border-b border-[#D4AF37]/20 bg-black/40 backdrop-blur-sm"
                data-testid="hero-ticker"
            >
                <div className="flex overflow-hidden whitespace-nowrap py-3">
                    <div className="animate-marquee flex shrink-0 gap-12 pr-12 text-xs tracking-[0.3em] uppercase font-bold">
                        {Array.from({ length: 2 }).map((_, k) => (
                            <React.Fragment key={k}>
                                <span className="text-[#D4AF37]">★ Bethpage Black</span>
                                <span className="text-white/40">●</span>
                                <span className="text-[#D4AF37]">★ Pebble Beach</span>
                                <span className="text-white/40">●</span>
                                <span className="text-[#D4AF37]">★ Oakmont</span>
                                <span className="text-white/40">●</span>
                                <span className="text-[#D4AF37]">★ Pinehurst No. 2</span>
                                <span className="text-white/40">●</span>
                                <span className="text-[#FF5A00]">$50,000 Grand Prize — Men</span>
                                <span className="text-white/40">●</span>
                                <span className="text-[#FF5A00]">$50,000 Grand Prize — Women</span>
                                <span className="text-white/40">●</span>
                                <span className="text-[#FF5A00]">$1,000 Per Verified Ace</span>
                                <span className="text-white/40">●</span>
                            </React.Fragment>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Hero;
